import { useState, useEffect } from 'react';
import { Bell, BellSlash, Check, Warning } from '@phosphor-icons/react';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  isPushSupported, 
  isSubscribed, 
  subscribeToPush, 
  unsubscribeFromPush
} from '../lib/push';

export function PushNotificationSettings() {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | 'default'>('default');
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      const isSupported = isPushSupported();
      setSupported(isSupported);
      
      if (isSupported) {
        setPermission(Notification.permission);
        const isSub = await isSubscribed();
        setSubscribed(isSub);
      }
      
      setLoading(false);
    };
    
    checkStatus();
  }, []);

  const handleToggle = async (enabled: boolean) => {
    setToggling(true);
    
    try {
      if (enabled) {
        await subscribeToPush();
        setSubscribed(true);
        setPermission('granted');
        toast.success('Pushnotiser aktiverade');
      } else {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success('Pushnotiser avaktiverade');
      }
    } catch (error: unknown) {
      console.error('Push toggle error:', error);
      if (error instanceof Error && error.message === 'Notification permission denied') {
        toast.error('Du måste tillåta notifikationer i din webbläsare');
      } else {
        toast.error('Kunde inte ändra notifikationsinställningar');
      }
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 bg-slate-50 rounded-lg animate-pulse">
        <div className="h-4 w-48 bg-slate-200 rounded mb-2" />
        <div className="h-3 w-64 bg-slate-200 rounded" />
      </div>
    );
  }

  if (!supported) {
    return (
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-center gap-3">
          <Warning size={20} className="text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-800">Stöds ej</p>
            <p className="text-xs text-amber-600">
              Din webbläsare stödjer inte pushnotifikationer.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-3">
          <BellSlash size={20} className="text-red-600" />
          <div>
            <p className="text-sm font-medium text-red-800">Notifikationer blockerade</p>
            <p className="text-xs text-red-600">
              Du har blockerat notifikationer. Ändra i webbläsarens inställningar för att aktivera.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white border border-border rounded-lg">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {subscribed ? (
            <Bell size={20} className="text-green-600" weight="fill" />
          ) : (
            <Bell size={20} className="text-slate-400" />
          )}
          <div>
            <p className="text-sm font-medium text-slate-900">Pushnotifikationer</p>
            <p className="text-xs text-slate-500">
              {subscribed 
                ? 'Du får notiser om nya ärenden och uppdateringar' 
                : 'Aktivera för att få direktnotiser'}
            </p>
          </div>
        </div>
        
        <Switch
          checked={subscribed}
          onCheckedChange={handleToggle}
          disabled={toggling}
          data-testid="push-toggle"
        />
      </div>
      
      {subscribed && (
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-xs text-slate-500 mb-2">Du får notiser för:</p>
          <ul className="space-y-1 text-xs text-slate-600">
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" />
              Nya felanmälningar (admin)
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" />
              Statusuppdateringar på dina ärenden
            </li>
            <li className="flex items-center gap-2">
              <Check size={14} className="text-green-500" />
              Meddelanden från underleverantörer
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
